import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { DraftBadge } from "@/components/DraftBadge";

// Server-stated reasons a briefing is not AI output; anything else reads as the generic error.
const UNAVAILABLE_KEY = {
  not_configured: "insights.unavailable.notConfigured",
  ai_error: "insights.unavailable.error",
};

export default function AiInsightsCard() {
  const t = useT("admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/tenant/insights/daily");
      setData(data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/tenant/insights/refresh");
      setData(data);
    } catch (e) {
      // Module disabled: hide silently
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isFallback = data?.source === "fallback";

  if (error) return null; // AI Agent disabled: render nothing

  return (
    <div
      data-testid="ai-insights-card"
      className="rounded-3xl p-7 bg-gradient-to-br from-sol-midnight via-sol-slate to-sol-midnight text-sol-ivory border border-sol-gold/20 shadow-lg relative overflow-hidden"
    >
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-sol-gold/10 blur-3xl" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-sol-sage/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-sol-gold/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-sol-gold" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sol-gold/80">{t("insights.title")}</div>
              <div className="text-sm text-sol-ivory/60">{t("insights.subtitle")}</div>
            </div>
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="insights-refresh"
            className="p-2 rounded-lg hover:bg-sol-ivory/10 transition disabled:opacity-50"
            title={t("insights.refresh")}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-sol-ivory/60">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("insights.generating")}
          </div>
        ) : data ? (
          <>
            {isFallback ? (
              <>
                <p className="text-base leading-relaxed text-sol-ivory" data-testid="insights-unavailable">
                  {t(UNAVAILABLE_KEY[data.fallback_reason] || UNAVAILABLE_KEY.ai_error)}
                </p>
                <dl className="mt-4 space-y-1 text-sm text-sol-ivory/80" data-testid="insights-fallback-facts">
                  {[
                    { label: t("insights.fallback.bookings7d"), value: data.facts?.bookings_last_7d },
                    { label: t("insights.fallback.newContacts7d"), value: data.facts?.new_contacts_7d },
                    { label: t("insights.fallback.leads"), value: data.facts?.current_leads },
                  ].map((f) => (
                    <div key={f.label} className="flex justify-between gap-4">
                      <dt>{f.label}</dt>
                      <dd className="font-semibold text-sol-gold">{f.value ?? 0}</dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : (
              <>
                <DraftBadge label={t("insights.draft")} className="mb-3" />
                <p className="text-base leading-relaxed text-sol-ivory whitespace-pre-wrap" data-testid="insights-summary">
                  {data.summary}
                </p>
              </>
            )}
            {data.facts && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-sol-ivory/10">
                {[
                  { label: t("insights.bookingsUpcoming"), value: data.facts.bookings_upcoming },
                  { label: t("insights.currentLeads"), value: data.facts.current_leads },
                  { label: t("insights.scheduledPosts"), value: data.facts.scheduled_posts },
                  { label: t("insights.automationRuns"), value: data.facts.automation_runs_7d },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="font-sol text-2xl font-semibold text-sol-gold">{s.value ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-sol-ivory/50 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-[10px] text-sol-ivory/30">
              {data.cached ? t("insights.cachedToday") : t("insights.justNow")}
              {!isFallback && <> · {t("insights.poweredBy")}</>}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
